
import { RevBreadcrumb } from '@ds/components/Breadcrumb'
import { RevButton } from '@ds/components/Button'
import { RevContainer } from '@ds/components/Container'
import { RevDivider } from '@ds/components/Divider'
import { RevRating } from '@ds/components/Rating'
import { IconQuality } from '@ds/icons/IconQuality'
import isEmpty from 'lodash/isEmpty'
import { mapGetters } from 'vuex'

import { getMetas } from '@core/helpers/head'
import { trackClick } from '@tracking/events'

import { logReviewsError } from '../../../helpers'
import RateFilter from '../../components/RateFilter'
import RateSort, { RATE_SORT_TYPE } from '../../components/RateSort'
import ReviewsLegalModal from '../../components/ReviewsLegalModal'
import ReviewsList from '../../components/ReviewsList'
import {
  REVIEWS_TYPES,
  getBreadcrumbRequest,
  getGlobalRateRequest,
  getRateDistributionRequest,
  getRatingCategoriesRequest,
  getReviewsListRequest,
} from '../../helpers'

import translations from './ReviewsLanding.translations'
import RatingCategories from './components/RatingCategories'
import SEOText from './components/SEOText'
import Tags from './components/Tags'
import {
  getGoBackTranslationKey,
  getNormalizedParams,
  getReviewsType,
  getRoutesCurrentState,
  getSEOTranslationKeys,
  getTitleTranslationKey,
} from './helpers'

export default {
  components: {
    IconQuality,
    RateFilter,
    RateSort,
    RatingCategories,
    RevButton,
    RevBreadcrumb,
    RevContainer,
    RevDivider,
    RevRating,
    ReviewsLegalModal,
    ReviewsList,
    SEOText,
    Tags,
  },

  layout: 'empty',

  async asyncData({ store, route, error }) {
    const type = getReviewsType(route)
    const params = getNormalizedParams(route)

    const requests = [
      store.dispatch('http/request', getBreadcrumbRequest(route)),
      store.dispatch('http/request', getGlobalRateRequest({ type, params })),
    ]

    const activeRoutes = getRoutesCurrentState({ route })
    requests.push(
      activeRoutes.PRODUCT
        ? store
            .dispatch('http/request', getRatingCategoriesRequest(route))
            .catch((err) => {
              logReviewsError({ tags: [route.name], err })

              return { payload: [] }
            })
        : Promise.resolve({
            payload: [],
          }),
    )

    try {
      const [
        { payload: breadcrumb = [] },
        { payload: globalRate = {} },
        { payload: ratingCategories = [] },
      ] = await Promise.all(requests)

      return {
        breadcrumb,
        globalRate,
        ratingCategories,
      }
    } catch (err) {
      logReviewsError({ tags: [route.name], err })

      return error({
        statusCode: 404,
        message: err.message,
      })
    }
  },

  data() {
    return {
      breadcrumb: [],
      currentPage: 1,
      globalRate: {},
      hasNextResults: true,
      isLoading: false,
      rateDistribution: [],
      ratingCategories: [],
      reviews: [],
    }
  },

  async fetch() {
    const [reviews, rateDistribution] = await Promise.all([
      this.fetchReviews(),
      this.fetchRateDistribution(),
    ])

    this.reviews = reviews
    this.rateDistribution = rateDistribution
  },

  head() {
    const { title, description } = getSEOTranslationKeys({
      route: this.$route,
      translations,
      pageTitle: this.ancestor?.title,
      rate: this.globalRate?.averageRate,
      count: this.globalRate?.reviewsCount,
    })

    return {
      title: this.$t(title.key, title.params),
      meta: [
        ...getMetas({
          title: this.$t(title.key, title.params),
          description: this.$t(description.key, description.params),
        }),
      ],
      link: [
        {
          rel: 'canonical',
          href: `${this.baseUrl}${this.$route.fullPath}`,
        },
      ],
    }
  },

  computed: {
    ...mapGetters({
      baseUrl: 'config/baseURL',
    }),

    translations: () => translations,

    RATE_SORT_TYPE: () => RATE_SORT_TYPE,

    REVIEWS_TYPES: () => REVIEWS_TYPES,

    currentReviewsType() {
      return getReviewsType(this.$route)
    },

    currentReviewsParams() {
      return getNormalizedParams(this.$route)
    },

    trackingInfo() {
      return {
        zone: this.TRACKING_ZONE,
        pageType: this.$route.name,
      }
    },

    reviewsListTrackingInfo() {
      return {
        ...this.trackingInfo,
        averageRate: this.globalRate.averageRate,
        reviewsCount: this.globalRate.reviewsCount,
      }
    },

    title() {
      const { key, params = {} } = getTitleTranslationKey({
        route: this.$route,
        breadcrumb: this.breadcrumb,
        translations,
      })

      return this.$t(key, params)
    },

    ancestor() {
      const previousBreadcrumbIndex = Math.max(
        0,
        (this.breadcrumb?.length || 0) - 2,
      )

      return this.breadcrumb[previousBreadcrumbIndex]
    },

    backButton() {
      const key = getGoBackTranslationKey({
        route: this.$route,
        translations,
      })

      return {
        label: this.$t(key),
        url: this.ancestor?.slug,
      }
    },

    shouldDisplayFetchMoreButton() {
      return !isEmpty(this.reviews) && this.hasNextResults
    },

    hasRatingCategories() {
      return !isEmpty(this.ratingCategories)
    },
  },

  watch: {
    '$route.query': {
      async handler() {
        this.reviews = await this.fetchReviews()
      },
    },
  },

  created() {
    this.TRACKING_ZONE = 'reviews'
  },

  methods: {
    trackClickEvent(name) {
      trackClick({
        ...this.trackingInfo,
        name,
      })
    },

    async getReviews(options = {}) {
      const { payload = {} } = await this.$store
        .dispatch(
          'http/request',
          getReviewsListRequest({
            type: this.currentReviewsType,
            params: this.currentReviewsParams,
            route: this.$route,
            ...options,
          }),
        )
        .catch((err) => {
          logReviewsError({ tags: [this.$route.name], err })

          return {
            payload: {
              results: [],
              next: null,
            },
          }
        })

      return payload
    },

    async fetchReviews() {
      const { results, next } = await this.getReviews()

      this.hasNextResults = !isEmpty(next)
      this.currentPage = 1

      return results || []
    },

    async fetchMoreReviews() {
      this.isLoading = true

      this.trackClickEvent('see_more_reviews')
      const { results, next } = await this.getReviews({
        page: this.currentPage + 1,
      })

      this.hasNextResults = !isEmpty(next)
      this.reviews = this.reviews.concat(results)
      this.currentPage += 1

      this.isLoading = false
    },

    async fetchRateDistribution() {
      const { payload } = await this.$store
        .dispatch(
          'http/request',
          getRateDistributionRequest({
            type: this.currentReviewsType,
            params: this.currentReviewsParams,
          }),
        )
        .catch((err) => {
          logReviewsError({ tags: [this.$route.name], err })

          return {
            payload: { statsInfo: [] },
          }
        })

      return payload.statsInfo
    },
  },
}
